<template>
  <div class="home-dashboard">
    <v-container fluid class="px-6 py-4">
      <!-- Super Admin Dashboard -->
      <template v-if="isSuperAdmin">
        <!-- Welcome Section -->
        <div class="welcome-section mb-6">
          <div class="welcome-content">
            <div class="welcome-text">
              <h1 class="welcome-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-view-dashboard</v-icon>
                Vijan.AI Marketplace
                <span class="welcome-subtitle-inline">AI Agents Management Hub</span>
              </h1>
              <p class="welcome-subtitle">
                Manage your AI agents marketplace, monitor performance, and drive growth across all industries
              </p>
            </div>
            <div class="welcome-illustration">
              <img 
                src="@/assets/content-mod-logo.svg" 
                alt="Vijan.AI Marketplace"
                width="120"
                height="120"
              />
            </div>
          </div>
        </div>

        <!-- Marketplace Stats Grid -->
        <v-row class="mb-6">
          <v-col cols="12" sm="6" md="3">
            <v-card class="stats-card" elevation="4" hover>
              <v-card-text class="stats-content">
                <div class="stats-icon-wrapper blue--bg">
                  <v-icon size="28" color="white">mdi-robot</v-icon>
                </div>
                <div class="stats-text">
                  <div class="stats-value">{{ marketplaceStats.total_agents || 0 }}</div>
                  <div class="stats-label">AI Agents</div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="stats-card" elevation="4" hover>
              <v-card-text class="stats-content">
                <div class="stats-icon-wrapper green--bg">
                  <v-icon size="28" color="white">mdi-package-variant</v-icon>
                </div>
                <div class="stats-text">
                  <div class="stats-value">{{ marketplaceStats.active_subscriptions || 0 }}</div>
                  <div class="stats-label">Active Subscriptions</div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="stats-card" elevation="4" hover>
              <v-card-text class="stats-content">
                <div class="stats-icon-wrapper orange--bg">
                  <v-icon size="28" color="white">mdi-office-building</v-icon>
                </div>
                <div class="stats-text">
                  <div class="stats-value">{{ marketplaceStats.total_companies || 0 }}</div>
                  <div class="stats-label">Companies</div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="stats-card" elevation="4" hover>
              <v-card-text class="stats-content">
                <div class="stats-icon-wrapper purple--bg">
                  <v-icon size="28" color="white">mdi-currency-usd</v-icon>
                </div>
                <div class="stats-text">
                  <div class="stats-value">${{ formatRevenue(marketplaceStats.monthly_revenue || 0) }}</div>
                  <div class="stats-label">Monthly Revenue</div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Quick Actions for Super Admin -->
        <v-row class="mb-6">
          <v-col cols="12">
            <v-card class="quick-actions-card" elevation="6">
              <v-card-title class="modern-card-title">
                <h2 class="card-title">
                  <v-icon class="mr-2">mdi-lightning-bolt</v-icon>
                  Marketplace Management
                </h2>
              </v-card-title>
              <v-card-text class="pa-6">
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <v-btn
                      block
                      large
                      color="primary"
                      class="action-btn"
                      to="/ai-agents"
                      elevation="2"
                    >
                      <v-icon left>mdi-robot</v-icon>
                      AI Agents
                    </v-btn>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-btn
                      block
                      large
                      color="success"
                      class="action-btn"
                      to="/subscriptions"
                      elevation="2"
                    >
                      <v-icon left>mdi-package-variant</v-icon>
                      Subscriptions
                    </v-btn>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-btn
                      block
                      large
                      color="info"
                      class="action-btn"
                      to="/companies"
                      elevation="2"
                    >
                      <v-icon left>mdi-office-building</v-icon>
                      Companies
                    </v-btn>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-btn
                      block
                      large
                      color="warning"
                      class="action-btn"
                      to="/coupons"
                      elevation="2"
                    >
                      <v-icon left>mdi-tag-multiple</v-icon>
                      Coupons
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Recent Activity & Top Agents -->
        <v-row>
          <v-col cols="12" md="6">
            <v-card class="activity-card" elevation="6">
              <v-card-title class="modern-card-title">
                <h3 class="card-title">
                  <v-icon class="mr-2">mdi-clock-outline</v-icon>
                  Recent Activity
                </h3>
              </v-card-title>
              <v-card-text class="pa-0">
                <div v-if="recentActivity.length === 0" class="empty-state pa-6">
                  <v-icon size="48" color="#64748b">mdi-clipboard-outline</v-icon>
                  <p class="mt-3 mb-0 text-center">No recent activity</p>
                </div>
                <div v-else class="activity-list">
                  <div
                    v-for="(activity, index) in recentActivity"
                    :key="index"
                    class="activity-item"
                  >
                    <v-avatar size="32" :color="getActivityColor(activity.type)" class="mr-3">
                      <v-icon small color="white">{{ getActivityIcon(activity.type) }}</v-icon>
                    </v-avatar>
                    <div class="activity-content">
                      <div class="activity-text">{{ activity.description }}</div>
                      <div class="activity-time">{{ formatTime(activity.created_at) }}</div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card class="top-agents-card" elevation="6">
              <v-card-title class="modern-card-title">
                <h3 class="card-title">
                  <v-icon class="mr-2">mdi-star</v-icon>
                  Top Performing Agents
                </h3>
              </v-card-title>
              <v-card-text class="pa-0">
                <div v-if="topAgents.length === 0" class="empty-state pa-6">
                  <v-icon size="48" color="#64748b">mdi-robot-outline</v-icon>
                  <p class="mt-3 mb-0 text-center">No agents available</p>
                </div>
                <div v-else class="agents-list">
                  <div
                    v-for="(agent, index) in topAgents"
                    :key="agent.id"
                    class="agent-item"
                  >
                    <div class="agent-rank">#{{ index + 1 }}</div>
                    <v-avatar size="32" class="mr-3">
                      <v-img
                        :src="agent.logo_url || require('../../assets/sapp-only-logo.png')"
                        :alt="agent.name"
                      />
                    </v-avatar>
                    <div class="agent-content">
                      <div class="agent-name">{{ agent.name }}</div>
                      <div class="agent-subscribers">{{ agent.subscribers || 0 }} subscribers</div>
                    </div>
                    <div class="agent-revenue">${{ agent.monthly_price || 0 }}/mo</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Admin Dashboard -->
      <template v-else>
        <!-- Welcome Section -->
        <div class="welcome-section mb-6">
          <div class="welcome-content">
            <div class="welcome-text">
              <h1 class="welcome-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-view-dashboard</v-icon>
                Welcome to Vijan.AI
                <span class="welcome-subtitle-inline">Your AI Agents Dashboard</span>
              </h1>
              <p class="welcome-subtitle">
                Access and manage your subscribed AI agents from one central location
              </p>
            </div>
            <div class="welcome-illustration">
              <img 
                src="@/assets/content-mod-logo.svg" 
                alt="Vijan.AI"
                width="120"
                height="120"
              />
            </div>
          </div>
        </div>

        <!-- AI Agents Cards for Admin -->
        <v-row class="mb-6">
          <v-col cols="12">
            <v-card class="ai-agents-card" elevation="6">
              <v-card-title class="modern-card-title">
                <h2 class="card-title">
                  <v-icon class="mr-2">mdi-robot</v-icon>
                  Your AI Agents
                </h2>
              </v-card-title>
              <v-card-text class="pa-6">
                <div v-if="availableAgents.length === 0" class="empty-agents-state">
                  <v-icon size="64" color="#64748b">mdi-robot-outline</v-icon>
                  <h3>No AI Agents Available</h3>
                  <p>Contact your administrator to get access to AI agents or browse available subscription plans.</p>
                  <v-btn color="primary" to="/available-subscriptions" class="mt-4">
                    <v-icon left>mdi-store</v-icon>
                    Browse Plans
                  </v-btn>
                </div>
                <v-row v-else>
                  <v-col
                    v-for="agent in availableAgents"
                    :key="agent.id"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="3"
                  >
                    <v-card class="agent-card" elevation="4" hover>
                      <div class="agent-header" :style="`background: ${agent.color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}`">
                        <v-avatar size="48" class="agent-logo">
                          <v-img
                            :src="agent.logo_url || require('../../assets/sapp-only-logo.png')"
                            :alt="agent.name"
                          />
                        </v-avatar>
                        <div class="agent-status" v-if="agent.is_active">
                          <v-chip color="success" dark x-small>
                            <v-icon left x-small>mdi-check-circle</v-icon>
                            Active
                          </v-chip>
                        </div>
                      </div>
                      <v-card-text class="agent-content pa-4">
                        <h3 class="agent-name">{{ agent.name }}</h3>
                        <p class="agent-description">{{ agent.description }}</p>
                        <div class="agent-pricing">
                          <span class="price">${{ agent.monthly_price || 0 }}</span>
                          <span class="period">/month</span>
                        </div>
                        <div class="agent-features" v-if="agent.features && agent.features.length">
                          <div class="features-title">Key Features:</div>
                          <ul class="features-list">
                            <li v-for="feature in agent.features.slice(0, 3)" :key="feature.id">
                              <v-icon x-small color="success">mdi-check</v-icon>
                              {{ feature.name }}
                            </li>
                          </ul>
                        </div>
                      </v-card-text>
                      <v-card-actions class="agent-actions pa-4">
                        <v-btn
                          v-if="agent.name === 'ContentMod AI'"
                          color="primary"
                          block
                          class="agent-action-btn"
                          to="/agents/contentmod-ai/dashboard"
                        >
                          <v-icon left>mdi-view-dashboard</v-icon>
                          Open Dashboard
                        </v-btn>
                        <v-btn
                          v-else
                          color="primary"
                          outlined
                          block
                          class="agent-action-btn"
                          disabled
                        >
                          <v-icon left>mdi-cog</v-icon>
                          Configure
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </div>
</template>
<script>
import axios from "@/axios";
import store from "@/store";

export default {
  name: "HomePage",
  data() {
    return {
      isLoading: false,
      stats: {
        total_posts: 0,
        pending_moderation: 0,
        approved_posts: 0,
        rejected_posts: 0,
        flagged_posts: 0,
        high_risk_posts: 0,
        avg_toxicity_score: 0,
        avg_spam_score: 0,
        avg_inappropriate_score: 0,
        avg_overall_score: 0,
      },
      marketplaceStats: {
        total_agents: 0,
        active_subscriptions: 0,
        total_companies: 0,
        monthly_revenue: 0,
      },
      recentActivity: [],
      topAgents: [],
      availableAgents: [],
    };
  },
  computed: {
    isSuperAdmin() {
      const role = store.getters["auth/roleName"];
      const company = store.getters["auth/company"];
      return role === "SUPER ADMIN" && (!company || !company.id);
    },
  },
  async mounted() {
    if (this.isSuperAdmin) {
      await this.fetchMarketplaceStats();
      await this.fetchRecentActivity();
      await this.fetchTopAgents();
    } else {
      await this.fetchStats();
      await this.fetchAvailableAgents();
    }
  },
  methods: {
    async fetchStats() {
      try {
        this.isLoading = true;
        const response = await axios.get('moderation/stats');
        this.stats = response.data.data;
      } catch (error) {
        console.error('Error fetching stats:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load dashboard statistics. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchMarketplaceStats() {
      try {
        this.isLoading = true;
        // Fetch marketplace overview stats
        const [agentsRes, subscriptionsRes, companiesRes] = await Promise.all([
          axios.get('/ai-agents?rowsPerPage=1'),
          axios.get('/subscriptions?rowsPerPage=1'),
          axios.get('/companies?rowsPerPage=1')
        ]);
        
        this.marketplaceStats = {
          total_agents: agentsRes.data.count || 0,
          active_subscriptions: subscriptionsRes.data.count || 0,
          total_companies: companiesRes.data.count || 0,
          monthly_revenue: this.calculateMonthlyRevenue(subscriptionsRes.data.data || []),
        };
      } catch (error) {
        console.error('Error fetching marketplace stats:', error);
        // Fallback to realistic sample data
        this.marketplaceStats = {
          total_agents: 8,
          active_subscriptions: 24,
          total_companies: 15,
          monthly_revenue: 12750,
        };
      } finally {
        this.isLoading = false;
      }
    },
    async fetchRecentActivity() {
      try {
        // Mock recent activity for now
        this.recentActivity = [
          {
            type: 'subscription',
            description: 'New subscription created: Enterprise Plan',
            created_at: new Date(Date.now() - 2 * 60 * 60 * 1000)
          },
          {
            type: 'agent',
            description: 'AI Agent "ContentMod AI" updated',
            created_at: new Date(Date.now() - 5 * 60 * 60 * 1000)
          },
          {
            type: 'company',
            description: 'Company "TechCorp" registered',
            created_at: new Date(Date.now() - 24 * 60 * 60 * 1000)
          }
        ];
      } catch (error) {
        console.error('Error fetching recent activity:', error);
      }
    },
    async fetchTopAgents() {
      try {
        const response = await axios.get('/ai-agents?rowsPerPage=5&sort=subscribers');
        this.topAgents = response.data.data || [];
      } catch (error) {
        console.error('Error fetching top agents:', error);
        // Mock data for demonstration - including ContentMod AI
        this.topAgents = [
          {
            id: 1,
            name: 'ContentMod AI',
            monthly_price: 99,
            subscribers: 45,
            logo_url: null
          },
          {
            id: 2,
            name: 'ChatBot Pro',
            monthly_price: 149,
            subscribers: 38,
            logo_url: null
          },
          {
            id: 3,
            name: 'Analytics AI',
            monthly_price: 199,
            subscribers: 32,
            logo_url: null
          },
          {
            id: 4,
            name: 'Email Assistant',
            monthly_price: 79,
            subscribers: 28,
            logo_url: null
          },
          {
            id: 5,
            name: 'Data Processor',
            monthly_price: 249,
            subscribers: 24,
            logo_url: null
          }
        ];
      }
    },
    async fetchAvailableAgents() {
      try {
        // Fetch only agents that the company has access to through their active subscriptions
        console.log('Fetching available agents with admin filter...');
        const response = await axios.get('/ai-agents?admin_filter=true&is_active=true');
        console.log('Available agents response:', response.data);
        this.availableAgents = response.data.data || [];
      } catch (error) {
        console.error('Error fetching available agents:', error);
        // No agents available if API fails and company has no subscriptions
        this.availableAgents = [];
      }
    },
    calculateAccuracy() {
      if (!this.stats.avg_overall_score) return 95.2;
      // Convert score to accuracy percentage (inverse relationship)
      const accuracy = (1 - this.stats.avg_overall_score) * 100;
      return Math.max(85, Math.min(99, accuracy)).toFixed(1);
    },
    calculateMonthlyRevenue(subscriptions) {
      return subscriptions.reduce((total, sub) => {
        return total + (sub.monthly_price || 0);
      }, 0);
    },
    formatRevenue(amount) {
      if (amount >= 1000000) {
        return (amount / 1000000).toFixed(1) + 'M';
      } else if (amount >= 1000) {
        return (amount / 1000).toFixed(1) + 'K';
      }
      return amount.toString();
    },
    formatTime(date) {
      const now = new Date();
      const diff = now - new Date(date);
      const hours = Math.floor(diff / (1000 * 60 * 60));
      
      if (hours < 1) {
        const minutes = Math.floor(diff / (1000 * 60));
        return `${minutes}m ago`;
      } else if (hours < 24) {
        return `${hours}h ago`;
      } else {
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
      }
    },
    getActivityIcon(type) {
      const icons = {
        subscription: 'mdi-package-variant',
        agent: 'mdi-robot',
        company: 'mdi-office-building',
        coupon: 'mdi-tag'
      };
      return icons[type] || 'mdi-information';
    },
    getActivityColor(type) {
      const colors = {
        subscription: 'success',
        agent: 'primary',
        company: 'info',
        coupon: 'warning'
      };
      return colors[type] || 'grey';
    },
  },
};
</script>

<style scoped>
.home-dashboard {
  min-height: calc(100vh - 64px);
}

/* Welcome Section */
.welcome-section {
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.welcome-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.welcome-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 30px;
}

.welcome-title {
  font-size: 36px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  line-height: 1.2;
}

.welcome-subtitle {
  font-size: 18px;
  color: #64748b;
  margin: 12px 0 0 0;
  font-weight: 400;
  max-width: 600px;
}

.welcome-illustration img {
  opacity: 0.9;
  transition: all 0.3s ease;
}

.welcome-illustration:hover img {
  opacity: 1;
  transform: scale(1.05);
}

/* Stats Cards */
.stats-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  background: white;
  overflow: hidden;
}

.stats-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 48px rgba(102, 126, 234, 0.15) !important;
}

.stats-content {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px !important;
}

.stats-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.orange--bg { background: linear-gradient(135deg, #ff9500 0%, #ff6b00 100%); }
.red--bg { background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%); }
.green--bg { background: linear-gradient(135deg, #51cf66 0%, #40c057 100%); }
.blue--bg { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.purple--bg { background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%); }

.stats-text {
  flex: 1;
}

.stats-value {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
  margin-bottom: 6px;
}

.stats-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Quick Actions Card */
.quick-actions-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
}

.card-title {
  font-size: 22px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.action-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  height: 56px !important;
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

/* Feature Cards */
.feature-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  background: white;
  height: 100%;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.15) !important;
}

.feature-icon {
  text-align: center;
}

.feature-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 12px 0;
  text-align: center;
}

.feature-description {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  text-align: center;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 960px) {
  .welcome-content {
    flex-direction: column;
    text-align: center;
  }
  
  .welcome-title {
    font-size: 28px;
    justify-content: center;
  }
  
  .welcome-section {
    padding: 30px 20px;
  }
  
  .stats-content {
    padding: 20px !important;
  }
  
  .stats-icon-wrapper {
    width: 56px;
    height: 56px;
  }
  
  .stats-value {
    font-size: 28px;
  }
}

@media (max-width: 600px) {
  .welcome-section {
    padding: 20px 16px;
  }
  
  .welcome-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }
  
  .stats-content {
    padding: 16px !important;
    gap: 16px;
  }
  
  .stats-icon-wrapper {
    width: 48px;
    height: 48px;
  }
  
  .stats-value {
    font-size: 24px;
  }
  
  .action-btn {
    height: 48px !important;
    margin-bottom: 12px;
  }
  
  .feature-card .pa-6 {
    padding: 20px !important;
  }
}

/* Super Admin Dashboard Specific Styles */
.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

.welcome-subtitle-inline {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

/* Activity Card */
.activity-card,
.top-agents-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
  height: 100%;
}

.activity-list,
.agents-list {
  max-height: 400px;
  overflow-y: auto;
}

.activity-item,
.agent-item {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}

.activity-item:hover,
.agent-item:hover {
  background: rgba(102, 126, 234, 0.05);
}

.activity-item:last-child,
.agent-item:last-child {
  border-bottom: none;
}

.activity-content,
.agent-content {
  flex: 1;
  margin-left: 12px;
}

.activity-text,
.agent-name {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.activity-time,
.agent-subscribers {
  font-size: 12px;
  color: #64748b;
}

.agent-rank {
  font-size: 14px;
  font-weight: 700;
  color: #667eea;
  min-width: 24px;
}

.agent-revenue {
  font-size: 12px;
  font-weight: 600;
  color: #10b981;
  text-align: right;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
}

.empty-state p {
  font-size: 14px;
  color: #64748b;
}

/* AI Agents Cards for Admin Dashboard */
.ai-agents-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.empty-agents-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
}

.empty-agents-state h3 {
  margin: 16px 0 8px 0;
  color: #2c3e50;
  font-size: 20px;
}

.empty-agents-state p {
  font-size: 14px;
  color: #64748b;
  max-width: 400px;
  margin: 0 auto 16px;
}

.agent-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.agent-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2) !important;
}

.agent-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-logo {
  border: 3px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
}

.agent-status {
  position: absolute;
  top: 12px;
  right: 12px;
}

.agent-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.agent-name {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 8px 0;
  text-align: center;
}

.agent-description {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  margin-bottom: 16px;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.agent-pricing {
  text-align: center;
  margin-bottom: 16px;
}

.agent-pricing .price {
  font-size: 24px;
  font-weight: 700;
  color: #10b981;
}

.agent-pricing .period {
  font-size: 14px;
  color: #64748b;
}

.agent-features {
  margin-top: auto;
}

.features-title {
  font-size: 12px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.features-list li {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  font-size: 12px;
  color: #2c3e50;
}

.agent-actions {
  background: #f8f9fa;
  border-top: 1px solid #e2e8f0;
  padding: 16px !important;
}

.agent-action-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  height: 40px !important;
}

/* Responsive adjustments for Admin AI Agents */
@media (max-width: 960px) {
  .welcome-subtitle-inline {
    display: block;
    margin-left: 0;
    margin-top: 8px;
    font-size: 14px;
  }
  
  .agent-header {
    padding: 16px;
  }
  
  .agent-name {
    font-size: 16px;
  }
  
  .agent-description {
    font-size: 12px;
  }
}

@media (max-width: 600px) {
  .empty-agents-state {
    padding: 40px 16px;
  }
  
  .empty-agents-state h3 {
    font-size: 18px;
  }
  
  .agent-card {
    margin-bottom: 16px;
  }
  
  .agent-pricing .price {
    font-size: 20px;
  }
}
</style>
